import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { FaArrowLeft, FaEdit, FaSave, FaPlus, FaCar } from "react-icons/fa";
import { gastosService, transportistasService, empleadosService, vehiculosService } from "../services/api";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";

const TransportistaDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transportistaId = searchParams.get("transportista_id");

  const isCreate = !transportistaId;

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [empleadosPrl, setEmpleadosPrl] = useState([]);
  const [selectedEmpleadoOption, setSelectedEmpleadoOption] = useState(null);
  const [detalle, setDetalle] = useState({
    NOMBRE: "",
    APELLIDOS: "",
    COD: "",
    DNI: "",
    TELEFONO: "",
    ES_EXTERNO: true,
    OBSERVACIONES: "",
    EMPLEADO_PRL_ID: "",
  });
  const [vehiculos, setVehiculos] = useState([]);

  useEffect(() => {
    if (!isCreate) {
      loadDetail();
    }
    loadEmpleados();
    // eslint-disable-next-line
  }, [transportistaId]);

  // Preseleccionar opción del empleado PRL cuando venga en el detalle
  useEffect(() => {
    const ensureSelectedEmpleado = async () => {
      const id = detalle.EMPLEADO_PRL_ID;
      if (id && !selectedEmpleadoOption) {
        try {
          const res = await empleadosService.getDetail(id);
          const emp = res.data?.empleado;
          if (emp) {
            setSelectedEmpleadoOption({ value: id, label: `${emp.NOMBRE || ''} ${emp.APELLIDOS || ''}`.trim() });
          }
        } catch (_e) {
          // Ignorar si no se puede cargar
        }
      }
    };
    ensureSelectedEmpleado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalle.EMPLEADO_PRL_ID]);

  const loadDetail = async () => {
    setLoading(true);
    try {
      const res = await transportistasService.getDetail(transportistaId);
      if (res.data.transportista) {
        const t = res.data.transportista;
        setDetalle({
          NOMBRE: t.NOMBRE || "",
          APELLIDOS: t.APELLIDOS || "",
          COD: t.COD || "",
          DNI: t.DNI || "",
          TELEFONO: t.TELEFONO || "",
          ES_EXTERNO: !!t.ES_EXTERNO,
          OBSERVACIONES: t.OBSERVACIONES || "",
          EMPLEADO_PRL_ID: t.EMPLEADO_PRL_ID || "",
        });
        if (t.ID) {
          await loadVehiculosByTransportista(t.ID);
        }
      }
    } catch (e) {
      toast.error("No se pudo cargar el transportista");
    } finally {
      setLoading(false);
    }
  };

  const loadVehiculosByTransportista = async (id) => {
    try {
      const res = await vehiculosService.listByTransportista(id);
      setVehiculos(res.data?.vehiculos || []);
    } catch (e) {
      setVehiculos([]);
    }
  };

  const loadEmpleados = async (cadena = "") => {
    try {
      const res = await transportistasService.getEmpleadosPrl(cadena);
      const lista = res.data?.empleados || [];
      setEmpleadosPrl(lista);
      // Si tenemos empleado PRL ya en detalle y no hay opción seleccionada, intenta fijarla por label o por ID
      if (!cadena && detalle.EMPLEADO_PRL_ID && !selectedEmpleadoOption) {
        const found = lista.find((e) => String(e.ID) === String(detalle.EMPLEADO_PRL_ID));
        if (found) setSelectedEmpleadoOption({ value: found.ID, label: found.EMPLEADO });
      }
    } catch (e) {
      // fallback a lista vacía
      setEmpleadosPrl([]);
    }
  };

  const loadEmpleadoOptions = async (inputValue) => {
    try {
      const res = await transportistasService.getEmpleadosPrl(inputValue || "");
      const lista = res.data?.empleados || [];
      return lista.map((emp) => ({ value: emp.ID, label: emp.EMPLEADO }));
    } catch (e) {
      return [];
    }
  };

  const handleChange = (field, value) => {
    setDetalle((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      if (isCreate) {
        const res = await transportistasService.create(detalle);
        if (res.data?.success) {
          toast.success("Transportista creado");
          navigate("/transportistas");
        } else {
          toast.error(res.data?.message || "Error al crear");
        }
      } else {
        const res = await transportistasService.update(transportistaId, detalle);
        if (res.data?.success) {
          toast.success("Transportista actualizado");
          setIsEditing(false);
        } else {
          toast.error(res.data?.message || "Error al actualizar");
        }
      }
    } catch (e) {
      toast.error("Error en la operación");
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos desde Empleado PRL seleccionado para rellenar campos
  const fillDatosFromEmpleadoPrl = async (empleadoId) => {
    try {
      const res = await empleadosService.getDetail(empleadoId);
      const emp = res.data?.empleado;
      if (emp) {
        setDetalle((prev) => ({
          ...prev,
          NOMBRE: emp.NOMBRE || "",
          APELLIDOS: emp.APELLIDOS || "",
          DNI: emp.DNI || "",
          TELEFONO: emp.TELEFONO_DEFECTO || "",
        }));
      }
    } catch (e) {
      toast.error("No se pudo cargar el empleado PRL seleccionado");
    }
  };

  const isFieldsLocked = useMemo(() => {
    // En creación: todo desbloqueado
    if (isCreate) return false;
    // En detalle: bloqueado si no estamos editando
    return !isEditing;
  }, [isCreate, isEditing]);

  const isDatosPersonalesLocked = useMemo(() => {
    // Si NO es externo, los datos personales van bloqueados y se muestra select de empleado PRL
    if (!detalle.ES_EXTERNO) return true;
    // Si es externo, se pueden editar nombre/apellidos/dni/telefono
    return isFieldsLocked ? true : false;
  }, [detalle.ES_EXTERNO, isFieldsLocked]);

  return (
    <>
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Button variant="outline-secondary" onClick={() => navigate("/transportistas")} className="me-3">
              <FaArrowLeft />
            </Button>
            <h4 className="mb-0">{isCreate ? "Nuevo Transportista" : "Detalle de Transportista"}</h4>
          </div>
          <div>
            {isCreate || isEditing ? (
              <Button variant="success" onClick={handleSave} disabled={loading} className="d-flex align-items-center gap-2">
                <FaSave /> {loading ? "Guardando..." : "Guardar"}
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setIsEditing(true)} className="d-flex align-items-center">
                <FaEdit />
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Externo:</strong></label>
                {isFieldsLocked ? (
                  <p className="mb-0">{detalle.ES_EXTERNO ? "Sí" : "No"}</p>
                ) : (
                  <Form.Check
                    type="switch"
                    id="es-externo"
                    checked={!!detalle.ES_EXTERNO}
                    onChange={(e) => handleChange("ES_EXTERNO", e.target.checked)}
                    label={detalle.ES_EXTERNO ? "Sí" : "No"}
                  />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Código:</strong></label>
                {isFieldsLocked ? (
                  <p className="mb-0">{detalle.COD || '-'}</p>
                ) : (
                  <Form.Control
                    value={detalle.COD}
                    onChange={(e) => handleChange("COD", e.target.value)}
                  />
                )}
              </div>
            </Col>
          </Row>

          {!detalle.ES_EXTERNO && (
            <Row className="mb-3">
              <Col md={6}>
                <div className="mb-3">
                  <label className="form-label"><strong>Empleado PRL:</strong></label>
                  {isFieldsLocked ? (
                    <p className="mb-0">{selectedEmpleadoOption?.label || '-'}</p>
                  ) : (
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      openMenuOnFocus
                      isClearable
                      loadOptions={loadEmpleadoOptions}
                      value={selectedEmpleadoOption}
                      onChange={(opt) => {
                        setSelectedEmpleadoOption(opt);
                        handleChange("EMPLEADO_PRL_ID", opt ? opt.value : "");
                        if (opt && !detalle.ES_EXTERNO) {
                          fillDatosFromEmpleadoPrl(opt.value);
                        }
                      }}
                      placeholder="Buscar y seleccionar empleado..."
                      noOptionsMessage={() => "Sin resultados"}
                    />
                  )}
                </div>
              </Col>
            </Row>
          )}

          <h5 className="mb-3">Datos personales</h5>
          <Row className="mb-3">
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Nombre:</strong></label>
                {isDatosPersonalesLocked ? (
                  <p className="mb-0">{detalle.NOMBRE || '-'}</p>
                ) : (
                  <Form.Control
                    value={detalle.NOMBRE}
                    onChange={(e) => handleChange("NOMBRE", e.target.value)}
                  />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Apellidos:</strong></label>
                {isDatosPersonalesLocked ? (
                  <p className="mb-0">{detalle.APELLIDOS || '-'}</p>
                ) : (
                  <Form.Control
                    value={detalle.APELLIDOS}
                    onChange={(e) => handleChange("APELLIDOS", e.target.value)}
                  />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>DNI:</strong></label>
                {isDatosPersonalesLocked ? (
                  <p className="mb-0">{detalle.DNI || '-'}</p>
                ) : (
                  <Form.Control
                    value={detalle.DNI}
                    onChange={(e) => handleChange("DNI", e.target.value)}
                  />
                )}
              </div>
            </Col>
            <Col md={3}>
              <div className="mb-3">
                <label className="form-label"><strong>Teléfono:</strong></label>
                {isDatosPersonalesLocked ? (
                  <p className="mb-0">{detalle.TELEFONO || '-'}</p>
                ) : (
                  <Form.Control
                    value={detalle.TELEFONO}
                    onChange={(e) => handleChange("TELEFONO", e.target.value)}
                  />
                )}
              </div>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <div className="mb-3">
                <label className="form-label"><strong>Observaciones:</strong></label>
                {isFieldsLocked ? (
                  <div className="text-wrap">{detalle.OBSERVACIONES || '-'}</div>
                ) : (
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={detalle.OBSERVACIONES}
                    onChange={(e) => handleChange("OBSERVACIONES", e.target.value)}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!isCreate && (
        <Card className="mt-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Vehículos</h5>
            <Button
              variant="outline-success"
              size="sm"
              className="d-flex align-items-center gap-2"
              onClick={() => navigate(`/vehiculo-detail?transportista_id=${transportistaId}`)}
            >
              <FaPlus /> <FaCar /> Añadir
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>Matrícula</th>
                    <th>Tipo</th>
                    <th>Fin seguro</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {vehiculos.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">No hay vehículos</td>
                    </tr>
                  ) : (
                    vehiculos.map((v) => (
                      <tr key={v.ID}>
                        <td>{v.MATRICULA}</td>
                        <td>{v.TIPO_VEHICULO}</td>
                        <td>{v.FECHA_FIN_SEGURO ? new Date(v.FECHA_FIN_SEGURO).toLocaleDateString('es-ES') : '-'}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={() => navigate(`/vehiculo-detail?vehiculo_id=${v.ID}`)}
                          >
                            <FaEdit />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default TransportistaDetail;


